import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../utils/apiClient';
import './HTML.css';

/**
 * HTML Emulator Component
 * 
 * This component handles HTML content in two ways:
 * 1. Single HTML file - When a standalone HTML file is uploaded as a ROM, it's loaded directly in an iframe
 * 2. ZIP archive - When a ZIP file is uploaded as a ROM, it is extracted during upload to a folder based on the game's ID,
 *    and the index.html file from that folder is loaded in an iframe
 * 
 * The component accepts both original and Chinese ROM versions.
 * 
 * Frame rate control is implemented via a server-side proxy that injects the control script
 * into the HTML content before serving it, avoiding cross-origin issues.
 * 
 * Launch options can be specified in the game's launchoptions column using the format:
 * --fps=60 --resolution=800x600 --htmlfile=game.html
 * 
 * Available launch options:
 * --fps=<number>           : Sets target frame rate (e.g., --fps=30)
 * --resolution=<w>x<h>     : Sets display resolution (e.g., --resolution=800x600)
 * --htmlfile=<filename>    : Overrides default index.html with specified file (e.g., --htmlfile=game.html)
 */
const HTML = ({ game, selectedRomVersion }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlPath, setHtmlPath] = useState('');
  const [isGameFocused, setIsGameFocused] = useState(false);
  const iframeRef = useRef(null);
  const isLoadingRef = useRef(false);
  const [frameStyle, setFrameStyle] = useState({});
  
  // Helper function for debug messages
  const addDebugMessage = (message) => {
    console.log('HTML Emulator Debug:', message);
  };
  
  // Parse launch options from game object
  const parseLaunchOptions = (launchOptions) => {
    if (!launchOptions) return { fps: null, resolution: null, htmlFile: null };
    
    const options = {};
    
    // Parse FPS setting
    const fpsMatch = launchOptions.match(/--fps=(\d+)/);
    if (fpsMatch && fpsMatch[1]) {
      options.fps = parseInt(fpsMatch[1], 10);
      addDebugMessage(`Setting FPS limit to: ${options.fps}`);
    }
    
    // Parse resolution setting
    const resolutionMatch = launchOptions.match(/--resolution=(\d+)x(\d+)/);
    if (resolutionMatch && resolutionMatch[1] && resolutionMatch[2]) {
      options.width = parseInt(resolutionMatch[1], 10);
      options.height = parseInt(resolutionMatch[2], 10);
      addDebugMessage(`Setting resolution to: ${options.width}x${options.height}`);
    }
    
    // Parse HTML file override setting
    const htmlFileMatch = launchOptions.match(/--htmlfile=([^\s]+)/);
    if (htmlFileMatch && htmlFileMatch[1]) {
      options.htmlFile = htmlFileMatch[1];
      addDebugMessage(`Using custom HTML file: ${options.htmlFile}`);
    }
    
    return options;
  };
  
  // Process the HTML ROM and set up appropriate URLs
  const processHtmlRom = async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      addDebugMessage('Processing HTML ROM');
      
      // Use the ROM path already selected in the game object
      const romToUse = game.rom;
      
      if (!romToUse) {
        throw new Error('No ROM specified for HTML emulator');
      }
      
      const gameId = game.id;
      
      addDebugMessage(`Using ROM: ${romToUse}`);
      
      // Parse launch options from game object
      const launchOptions = parseLaunchOptions(game.launchoptions);
      
      // Set frame style based on resolution
      if (launchOptions.width && launchOptions.height) {
        setFrameStyle({
          width: `${launchOptions.width}px`,
          height: `${launchOptions.height}px`,
          minHeight: `${launchOptions.height}px`,
          margin: '0 auto',
          flex: 'none'
        });
      }
      
      // Decide which URL to use based on whether FPS control is needed
      let gameUrl;
      
      // Detect if the ROM is a plain HTML file (not a zip extracted to roms/html/{id}/)
      const isDirectHtmlFile = /\.(html|htm)$/i.test(romToUse);
      
      if (isDirectHtmlFile) {
        // Plain HTML file: load it directly from its stored path
        if (launchOptions.fps) {
          // For FPS control with a direct file, derive a pseudo gameId path
          gameUrl = `${API_BASE_URL}/html-game-direct?path=${encodeURIComponent(romToUse)}&fpsMethod=raf&targetFps=${launchOptions.fps}`;
          addDebugMessage(`Using server-side FPS control for direct HTML file: ${gameUrl}`);
        } else {
          gameUrl = `${API_BASE_URL}${romToUse}`;
          addDebugMessage(`Using direct HTML file URL: ${gameUrl}`);
        }
      } else {
        // ZIP-extracted game: load from roms/html/{gameId}/ directory
        // Determine which HTML file to load - default to index.html if not specified
        const htmlFile = launchOptions.htmlFile || 'index.html';
        addDebugMessage(`Using HTML file: ${htmlFile}`);
        
        if (launchOptions.fps) {
          // Use the server-side endpoint with FPS control
          gameUrl = `${API_BASE_URL}/html-game/${gameId}/${htmlFile}?fpsMethod=raf&targetFps=${launchOptions.fps}`;
          addDebugMessage(`Using server-side FPS control with target FPS ${launchOptions.fps}: ${gameUrl}`);
        } else {
          // Use the direct URL without FPS control
          gameUrl = `${API_BASE_URL}/roms/html/${gameId}/${htmlFile}`;
          addDebugMessage(`Using direct URL without FPS control: ${gameUrl}`);
        }
      }
      
      // Add a timestamp to prevent caching issues
      const timestamp = Date.now();
      // Use & if the URL already contains parameters, otherwise use ?
      const paramSeparator = gameUrl.includes('?') ? '&' : '?';
      gameUrl = `${gameUrl}${paramSeparator}t=${timestamp}`;
      
      setHtmlPath(gameUrl);
      
      // Verify the file is accessible
      try {
        const testResponse = await fetch(gameUrl, { 
          method: 'HEAD',
          cache: 'no-cache',
        });
        
        if (!testResponse.ok) {
          addDebugMessage(`Warning: HTML file may not be accessible. Status: ${testResponse.status}`);
          throw new Error(`HTML file not accessible. Status: ${testResponse.status}`);
        } else {
          addDebugMessage(`HTML file is accessible`);
        }
      } catch (err) {
        addDebugMessage(`Error accessing HTML file: ${err.message}`);
        throw new Error(`Could not access HTML file: ${err.message}`);
      }
      
      setLoading(false);
      addDebugMessage('HTML processing completed successfully');
    } catch (err) {
      console.error('Error processing HTML ROM:', err);
      setError(`Failed to process HTML content: ${err.message}`);
      setLoading(false);
      addDebugMessage(`Error: ${err.message}`);
    } finally {
      isLoadingRef.current = false;
    }
  };
  
  // Focus handler for the iframe
  const handleFocus = () => {
    setIsGameFocused(true);
    addDebugMessage('Game focused');
  };

  // Blur handler for the iframe
  const handleBlur = () => {
    setIsGameFocused(false);
    addDebugMessage('Game blurred');
  };

  // Setup keyboard focus and event handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // If the game is not focused and a key is pressed, focus the iframe
      if (!isGameFocused && iframeRef.current) {
        iframeRef.current.focus();
      }
    };

    // Add global keyboard event listener to ensure keys are passed to the game
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isGameFocused]);
  
  useEffect(() => {
    processHtmlRom();
    
    // Capture the current iframe ref value for cleanup
    const currentIframe = iframeRef.current;
    
    // Cleanup function
    return () => {
      // Reset loading state when component unmounts or dependencies change
      isLoadingRef.current = false;
      
      // Clear iframe src to stop any ongoing loading
      if (currentIframe) {
        currentIframe.src = 'about:blank';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.rom, selectedRomVersion]); // processHtmlRom is intentionally excluded
  
  if (loading) {
    return (
      <div className="html-loading">
        <div className="loading-message">Loading HTML content...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="html-error">
        <div className="error-message">{error}</div>
      </div>
    );
  }
  
  return (
    <div 
      className={`html-container ${isGameFocused ? 'game-focused' : ''}`}
      onClick={() => {
        if (iframeRef.current && !isGameFocused) {
          iframeRef.current.focus();
        }
      }}
    >
      <iframe
        ref={iframeRef}
        src={htmlPath}
        title={game.titleeng || game.titlechn}
        className="html-frame emulator-frame"
        style={frameStyle}
        sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-pointer-lock"
        allow="autoplay; fullscreen; pointer-lock"
        allowFullScreen
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex="0" // Ensure iframe can receive keyboard focus
      />
    </div>
  );
};

HTML.propTypes = {
  game: PropTypes.object.isRequired,
  selectedRomVersion: PropTypes.string.isRequired
};

export default HTML;